import { useAppSelector } from '@casops/store/hooks';
import { DateTime, Duration } from 'luxon';
import { useCallback } from 'react';
import { parseDurationFromSeconds } from '@cas/hooks/dateTimeFormatting';
import i18n from '@casops/i18n';

const localeFormat: Intl.DateTimeFormatOptions = { ...DateTime.DATETIME_MED, month: 'short' };
const timeAndDateWithMillisecondsFormat: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED_WITH_SECONDS,
  hour12: false,
  fractionalSecondDigits: 3,
};
const timeAndDateWithSecondsFormat: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED_WITH_SECONDS,
  hour12: false,
};

export interface LocaleFormatter {
  showTimeAndDate: (dt: DateTime) => string;
  showDateOnly: (dt: DateTime) => string;
  showTimeOnly: (dt: DateTime, hideSeconds?: boolean) => string;
  showTimeOnlyFromMillis: (millis: number) => string;
  showHourOnly: (dt: DateTime) => string;
  showRelative: (dt: DateTime) => string | null;
  showTimeZoneOffset: () => string;
  showTimeAndDateFromIso: (isoTime: string) => string;
  showTimeAndDateWithMillisecondsFromIso: (isoTime: string) => string;
  showTimeAndDateWithSecondsFromIso: (isoTime: string) => string;
  showDateOnlyFromIso: (isoTime: string) => string;
  showTimeOnlyFromIso: (isoTime: string, hideSeconds?: boolean) => string;
  showDurationFromSeconds: (seconds: number) => string;
  showRelativeFromIso: (isoTime: string) => string | null;
  parseFromIso: (isoTime: string) => DateTime;
  parseDurationFromSeconds: (seconds: number) => Duration;
  getNumberText: (value: number, fractionDigits?: number) => string;
  getNumberPercentText: (value: number) => string;
  showTimeAndDateWithSecondsFromSeconds: (seconds: number) => string;
  showTimeAndDateWithSecondsFromMillis: (millis: number) => string;
  showTimeAndDateWithMillisFromMillis: (millis: number) => string;
}

export const useLocaleFormatter = (): LocaleFormatter => {
  const { dateTimeNumberFormat, timezone } = useAppSelector(state => state.settings.localization);

  const parseDurationFromSecondsCB = useCallback((seconds: number) => {
    return parseDurationFromSeconds(seconds, i18n.language);
  }, []);

  const getNumberPercentText = (value: number) => {
    return new Intl.NumberFormat(dateTimeNumberFormat, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Relative times and durations shown in LANGUAGE, not time/date format since they contain human-readable text (e.g. "minutes ago").
  return {
    showTimeAndDate: (dt: DateTime) =>
      dt.toLocaleString(localeFormat, { locale: dateTimeNumberFormat }),
    showDateOnly: (dt: DateTime) =>
      dt.toLocaleString(DateTime.DATE_MED, { locale: dateTimeNumberFormat }),
    showTimeOnly: (dt: DateTime, hideSeconds?: boolean) =>
      dt.toLocaleString(hideSeconds ? DateTime.TIME_SIMPLE : DateTime.TIME_WITH_SECONDS, {
        locale: dateTimeNumberFormat,
      }),
    showTimeOnlyFromMillis: (millis: number) =>
      DateTime.fromMillis(millis, { zone: timezone }).toLocaleString(
        { ...DateTime.TIME_WITH_SECONDS, fractionalSecondDigits: 3 },
        { locale: dateTimeNumberFormat },
      ),
    showHourOnly: (dt: DateTime) => dt.setLocale(dateTimeNumberFormat).toFormat('t'),
    showRelative: (dt: DateTime) => dt.setLocale(i18n.language).toRelative(),
    showTimeAndDateFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: timezone }).toLocaleString(localeFormat, {
        locale: dateTimeNumberFormat,
      }),
    showTimeAndDateWithMillisecondsFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: timezone }).toLocaleString(
        timeAndDateWithMillisecondsFormat,
        { locale: dateTimeNumberFormat },
      ),
    showTimeAndDateWithSecondsFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: timezone }).toLocaleString(timeAndDateWithSecondsFormat, {
        locale: dateTimeNumberFormat,
      }),
    showDateOnlyFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: timezone }).toLocaleString(DateTime.DATE_MED, {
        locale: dateTimeNumberFormat,
      }),
    showTimeOnlyFromIso: (isoTime: string, hideSeconds?: boolean) =>
      DateTime.fromISO(isoTime, { zone: timezone }).toLocaleString(
        hideSeconds ? DateTime.TIME_SIMPLE : DateTime.TIME_WITH_SECONDS,
        {
          locale: dateTimeNumberFormat,
        },
      ),
    showDurationFromSeconds: (seconds: number) =>
      parseDurationFromSecondsCB(seconds).toHuman({ unitDisplay: 'narrow' }),
    showTimeZoneOffset: () =>
      DateTime.local().setZone(i18n.language).setLocale(dateTimeNumberFormat).toFormat('ZZ'),
    showRelativeFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: timezone }).setLocale(i18n.language).toRelative(),
    parseFromIso: (isoTime: string) => DateTime.fromISO(isoTime, { zone: timezone }),
    parseDurationFromSeconds: parseDurationFromSecondsCB,
    getNumberText: (value: number, fractionDigits?: number) =>
      fractionDigits !== undefined
        ? value.toLocaleString(dateTimeNumberFormat, { maximumFractionDigits: fractionDigits })
        : value.toLocaleString(dateTimeNumberFormat),
    getNumberPercentText,
    showTimeAndDateWithSecondsFromSeconds: (seconds: number) =>
      DateTime.fromSeconds(seconds, { zone: timezone }).toLocaleString(
        timeAndDateWithSecondsFormat,
        { locale: dateTimeNumberFormat },
      ),
    showTimeAndDateWithSecondsFromMillis: (millis: number) =>
      DateTime.fromMillis(millis, { zone: timezone }).toLocaleString(timeAndDateWithSecondsFormat, {
        locale: dateTimeNumberFormat,
      }),
    showTimeAndDateWithMillisFromMillis: (millis: number) =>
      DateTime.fromMillis(millis, { zone: timezone }).toLocaleString(
        { ...DateTime.DATETIME_MED_WITH_SECONDS, fractionalSecondDigits: 3 },
        { locale: dateTimeNumberFormat },
      ),
  };
};
